import { z } from 'zod';
import { apiClient } from '../lib/api';
import { buildApiUrl } from '../utils/apiUrl';
import {
	ApiResponseSchema,
	PaginatedApiResponseSchema,
} from '../schemas';
import { UniversalChartDataSchema, NivoLineChartDataSchema } from '../schemas/chartSchema';

export const MajorSchema = z.object({
	maNganh: z.string(),
	tenNganh: z.string(),
	maKhoa: z.string(),
	tenKhoa: z.string().optional(),
	doUuTien: z.number().optional(),
	chiTieu: z.record(z.string(), z.number()).nullable().optional(),
	createdAt: z.string().optional(),
	updatedAt: z.string().optional(),
}).strip();

export const CreateMajorSchema = MajorSchema.pick({
	maNganh: true,
	tenNganh: true,
	maKhoa: true,
}).extend({
	doUuTien: z.number().optional(),
	chiTieu: z.record(z.string(), z.number()).optional(),
});

export const UpdateMajorSchema = MajorSchema.pick({
	tenNganh: true,
	maKhoa: true,
}).extend({
	doUuTien: z.number().optional(),
	chiTieu: z.record(z.string(), z.number()).optional(),
});

export const MajorYearStatsSchema = z.object({
	maNganh: z.string(),
	tenNganh: z.string(),
	maKhoa: z.string(),
	tenKhoa: z.string().optional(),
	yearData: z.record(z.string(), z.number()),
	quotaData: z.record(z.string(), z.number()).optional(),
}).strip();

export const MajorStatsResponseSchema = z.object({
	years: z.array(z.number()),
	stats: z.array(MajorYearStatsSchema),
	total: z.number()
}).strip();

export const MajorOverviewStatsSchema = z.object({
	totalMajors: z.number(),
	majorDetails: z.array(z.object({
		maNganh: z.string(),
		tenNganh: z.string(),
		studentCount: z.number(),
		facultyCode: z.string(),
		facultyName: z.string(),
		doUuTien: z.number()
	}).strip())
}).strip();

export const MajorAdmissionStatsResponseSchema = ApiResponseSchema(MajorStatsResponseSchema);

export const majorApi = {
	// Get all majors with pagination support
	getMajors: async (params?: URLSearchParams) => {
		const url = buildApiUrl('/majors', params);
		const response = await apiClient.get(url);
		return PaginatedApiResponseSchema(MajorSchema.array()).parse(response.data);
	},

	// CRUD operations for majors
	createMajor: async (data: z.infer<typeof CreateMajorSchema>) => {
		const response = await apiClient.post('/majors', data);
		return ApiResponseSchema(MajorSchema).parse(response.data);
	},

	updateMajor: async (id: string, data: z.infer<typeof UpdateMajorSchema>) => {
		const response = await apiClient.put(`/majors/${id}`, data);
		return ApiResponseSchema(MajorSchema).parse(response.data);
	},

	deleteMajor: async (id: string): Promise<{ message: string }> => {
		const response = await apiClient.delete<{ message: string }>(`/majors/${id}`);
		return response.data;
	},

	// Get major yearly stats
	getMajorStats: async (params?: URLSearchParams) => {
		const url = buildApiUrl('/majors/stats/admission', params);
		const response = await apiClient.get(url);
		return PaginatedApiResponseSchema(MajorStatsResponseSchema).parse(response.data);
	},

	// Get major overview stats
	getMajorOverview: async (params?: URLSearchParams) => {
		const url = buildApiUrl('/stats/majors/overview', params);
		const response = await apiClient.get(url);
		return ApiResponseSchema(MajorOverviewStatsSchema).parse(response.data);
	},

	// Get major chart statistics
	getMajorChartStats: async (params?: URLSearchParams) => {
		const url = buildApiUrl('/majors/stats/chart-data', params);
		const response = await apiClient.get(url);
		return ApiResponseSchema(UniversalChartDataSchema).parse(response.data);
	},

	getNivoLineChartData: async (params?: URLSearchParams) => {
		const url = buildApiUrl('/majors/stats/nivo-chart-data', params);
		const response = await apiClient.get(url);
		return ApiResponseSchema(NivoLineChartDataSchema).parse(response.data);
	},

	// Export majors to Excel
	exportToExcel: async (params?: URLSearchParams): Promise<void> => {
		return apiClient.exportToExcel('/majors/export/excel', params);
	},
};